<template>
  <v-card class="elevation-0">
    <v-dialog v-model="loading" persistent width="300">
      <v-card color="primary" dark>
        <v-card-text>
          Loading...
          <v-progress-linear indeterminate color="white" class="mb-0"></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-container>
      <span class="text-h6">Task Relationships</span>
      <p class="subtitle" v-if="readonly ? false : !noRelationships">Select any relationships you will like to remove</p>

      <p><strong>Task:</strong> {{ task.name }}</p>
      <p><strong>Required in order to:</strong> {{ task.taskListType && task.taskListType.value }}</p>
      <p><strong>Step:</strong> {{ task.step }}</p>
      <p><strong>Owner:</strong> {{ task.owner && task.owner.displayName }}</p>
      <p>
        <strong>Related Change Type:</strong>
        {{
          task.relatedChangeType && relatedChangeTypeMap[task.relatedChangeType]
            ? relatedChangeTypeMap[task.relatedChangeType].value
            : ''
        }}
      </p>

      <v-alert type="info" v-if="noRelationships">
        This task has no relationships
      </v-alert>
      <v-expansion-panels dense0>
        <v-expansion-panel v-for="(items, propertyName) in relationshipsKeyValue" :key="propertyName">
          <v-expansion-panel-title>{{ configurationDisplayNames[propertyName] }}</v-expansion-panel-title>
          <v-expansion-panel-text>
            <v-data-table :headers="headers" :items="getItems(propertyName)" dense show-select>
              <template v-slot:top>
                <v-row dense class="px-3">
                  <v-col cols="12">
                    <v-text-field
                      label="Search"
                      v-model="search[propertyName]"
                      clearable
                      hide-details
                      prepend-inner-icon="search"
                      maxlength="200"
                    >
                    </v-text-field>
                  </v-col>
                </v-row>
              </template>
              <template v-slot:header.data-table-select="{ on, props }">
                <v-checkbox
                  color="purple"
                  :value="areAllItemsSelectedByKey(propertyName)"
                  @change="toggleAll($event, propertyName)"
                  v-if="!readonly"
                  hide-details
                ></v-checkbox>
              </template>
              <template v-slot:item="props">
                <tr>
                  <td>
                    <v-checkbox
                      color="green"
                      v-if="!readonly"
                      v-model="props.item.isSelected"
                      @change="addItem(propertyName, props.item)"
                      hide-details
                    ></v-checkbox>
                  </td>
                  <td>{{ props.item.value }}</td>
                </tr>
              </template>
            </v-data-table>
          </v-expansion-panel-text>
        </v-expansion-panel>
        <v-expansion-panel v-if="changeCategoryMocTypes.length">
          <v-expansion-panel-title>Change Category MOC Types</v-expansion-panel-title>
          <v-expansion-panel-text>
            <v-data-table :headers="headers" :items="changeCategoryMocTypes" dense show-select>
              <template v-slot:top>
                <v-row dense class="px-3">
                  <v-col cols="12">
                    <v-text-field
                      label="Search"
                      v-model="search['changeCategoryMocTypes']"
                      clearable
                      hide-details
                      prepend-inner-icon="search"
                      maxlength="200"
                    >
                    </v-text-field>
                  </v-col>
                </v-row>
              </template>
              <template v-slot:header.data-table-select="{ on, props }">
                <v-checkbox
                  :value="areAllChangeCategoryMocTypeItemsSelected"
                  color="purple"
                  v-if="!readonly"
                  @change="toggleAllChangeCategoryMocTypes($event, 'changeCategoryMocTypes')"
                  hide-details
                ></v-checkbox>
              </template>
              <template v-slot:item="props">
                <tr>
                  <td>
                    <v-checkbox
                      color="primary"
                      hide-details
                      v-if="!readonly"
                      v-model="props.item.isSelected"
                      @change="addChangeCategoryMocTypeItem(props.item)"
                    ></v-checkbox>
                  </td>
                  <td>{{ props.item.value }}</td>
                </tr>
              </template>
            </v-data-table>
          </v-expansion-panel-text>
        </v-expansion-panel>
        <v-expansion-panel v-if="qaRelationships.length">
          <v-expansion-panel-title>Responses Relationships</v-expansion-panel-title>
          <v-expansion-panel-text>
            <v-data-table :headers="headers" :items="qaRelationships" dense show-select>
              <template v-slot:top>
                <v-row dense class="px-3">
                  <v-col cols="12">
                    <v-text-field
                      label="Search"
                      v-model="search['qaRelationships']"
                      clearable
                      hide-details
                      prepend-inner-icon="search"
                      maxlength="200"
                    >
                    </v-text-field>
                  </v-col>
                </v-row>
              </template>
              <template v-slot:header.data-table-select="{ on, props }">
                <v-checkbox
                  :value="areAllQaRelationshipsItemsSelected"
                  color="purple"
                  v-if="!readonly"
                  @change="toggleAllQARelationships($event, 'qaRelationships')"
                  hide-details
                ></v-checkbox>
              </template>
              <template v-slot:item="props">
                <tr>
                  <td>
                    <v-checkbox
                      @change="addItem('qaRelationships', props.item)"
                      color="primary"
                      hide-details
                      v-if="!readonly"
                      v-model="props.item.isSelected"
                    ></v-checkbox>
                  </td>
                  <td>{{ props.item.value }}</td>
                </tr>
              </template>
            </v-data-table>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
      <div class="mt-3">
        <v-btn @click="cancel()">Close</v-btn>
        <v-btn
          v-if="readonly ? false : !noRelationships"
          @click="removeSelectedRelationships()"
          color="red"
          class="ml-2 white--text"
          :disabled="disableRemoveButton"
          >Remove selected relationships</v-btn
        >
      </div>
    </v-container>
    <toaster ref="toaster"></toaster>
  </v-card>
</template>

<script>
import AdminService from '@/services/admin.service'
import userReplacementMixins from '@/mixins/userReplacementMixins'
import { ChangeTypes } from '@/common/types'
import Toaster from '@/components/Toaster'

export default {
  name: 'TaskRelationship',
  mixins: [userReplacementMixins],
  components: {
    Toaster
  },
  props: {
    taskId: String,
    communityAbbreviation: String,
    close: Function,
    readonly: Boolean
  },
  data() {
    return {
      relationships: {},
      selected: {
        changeCategoryMocTypes: []
      },
      headers: [
        {
          title: 'Name',
          key: 'value'
        }
      ],
      loading: false,
      task: {},
      search: {},
      relatedChangeTypeMap: ChangeTypes.properties
    }
  },
  computed: {
    relationshipsKeyValue() {
      const result = Object.keys(this.relationships).filter(
        k => k !== 'changeCategoryMocTypes' && k !== 'qaRelationships' && this.relationships[k].length > 0
      )
      let relationship = {}
      result.forEach(key => {
        relationship[key] = this.relationships[key]
      })
      return relationship
    },
    noRelationships() {
      const result =
        this.relationships &&
        Object.keys(this.relationships).filter(r => {
          const value = this.relationships[r]
          if (Array.isArray(value) && value.length > 0) {
            return true
          } else if (!Array.isArray(value) && Object.keys(value).length > 0) {
            return true
          }
          return false
        }).length === 0
      return result
    },
    disableRemoveButton() {
      const keys = Object.keys(this.selected)
      return keys.length === 0 || keys.every(k => this.selected[k].length === 0)
    },
    changeCategoryMocTypes() {
      const key = 'changeCategoryMocTypes'
      let result = (this.relationships[key] || []).map(changeCategoryMocType => {
        const changeCategoryId = changeCategoryMocType.item1.key
        const mocTypeId = changeCategoryMocType.item2.key
        return {
          changeCategoryId: changeCategoryId,
          mocTypeId: mocTypeId,
          value: `${changeCategoryMocType.item1.value} - ${changeCategoryMocType.item2.value}`,
          isSelected:
            this.selected.changeCategoryMocTypes.findIndex(
              s => s.changeCategoryId === changeCategoryId && s.mocTypeId === mocTypeId
            ) > -1
        }
      })

      if (
        this.search.changeCategoryMocTypes &&
        this.search.changeCategoryMocTypes.trim() &&
        this.search.changeCategoryMocTypes.trim().length >= 3
      ) {
        result = result.filter(t =>
          t.value
            .trim()
            .toLowerCase()
            .includes(this.search.changeCategoryMocTypes.trim().toLowerCase())
        )
      }

      return result
    },
    qaRelationships() {
      let result = Object.keys(this.relationships.qaRelationships || []).map(key => {
        return {
          key,
          value: this.relationships.qaRelationships[key].join(' & '),
          isSelected: this.selected.qaRelationships.findIndex(s => s.key === key) > -1
        }
      })

      if (this.search.qaRelationships && this.search.qaRelationships.trim() && this.search.qaRelationships.trim().length >= 3) {
        result = result.filter(t =>
          t.value
            .trim()
            .toLowerCase()
            .includes(this.search.qaRelationships.trim().toLowerCase())
        )
      }

      return result
    },
    areAllQaRelationshipsItemsSelected() {
      const key = 'qaRelationships'
      return (
        Object.keys(this.relationships[key]).length > 0 &&
        (this.selected[key] || []).length === Object.keys(this.relationships[key]).length
      )
    },
    areAllChangeCategoryMocTypeItemsSelected() {
      const key = 'changeCategoryMocTypes'
      const result =
        this.changeCategoryMocTypes.length > 0 && (this.selected[key] || []).length === this.changeCategoryMocTypes.length
      return result
    }
  },
  methods: {
    getItems(propertyName) {
      let result = this.relationshipsKeyValue[propertyName].map(p => {
        return {
          ...p,
          isSelected: this.selected[propertyName].findIndex(s => s.key === p.key) > -1
        }
      })

      if (this.search[propertyName] && this.search[propertyName].trim() && this.search[propertyName].trim().length >= 3) {
        result = result.filter(t =>
          t.value
            .trim()
            .toLowerCase()
            .includes(this.search[propertyName].trim().toLowerCase())
        )
      }

      return result
    },
    changeCategoryMocTypeValueComparator(a, b) {
      return (
        this.selected.changeCategoryMocTypes.length && a.changeCategoryId === b.changeCategoryId && a.mocTypeId === b.mocTypeId
      )
    },
    valueComparator(key) {
      return (a, b) => {
        return this.selected[key].length && a.key === b.key
      }
    },
    toggleAll(value, key) {
      if (value) {
        this.selected[key] = [...this.getItems(key)]
      } else {
        this.selected[key] = []
      }
    },
    toggleAllQARelationships(value, key) {
      if (value) {
        this.selected[key] = this.qaRelationships
      } else {
        this.selected[key] = []
      }
    },
    toggleAllChangeCategoryMocTypes(value, key) {
      if (value) {
        this.selected[key] = this.changeCategoryMocTypes
      } else {
        this.selected[key] = []
      }
    },
    addChangeCategoryMocTypeItem(item) {
      const index = this.selected.changeCategoryMocTypes.findIndex(
        s => s.changeCategoryId === item.changeCategoryId && s.mocTypeId === item.mocTypeId
      )
      if (index === -1) {
        this.selected.changeCategoryMocTypes.push(item)
      } else {
        this.selected.changeCategoryMocTypes.splice(index, 1)
      }
    },
    addItem(key, item) {
      const index = this.selected[key].findIndex(s => s.key === item.key)
      if (index === -1) {
        this.selected[key].push(item)
      } else {
        this.selected[key].splice(index, 1)
      }
    },
    areAllItemsSelectedByKey(key) {
      const result = (this.selected[key] || []).length === this.getItems(key).length
      return result
    },
    removeSelectedRelationships() {
      this.loading = true
      const body = { ...this.selected }
      body['changeCategoryMocTypes'] = body['changeCategoryMocTypes'].map(c => ({
        key: c.changeCategoryId,
        value: c.mocTypeId
      }))
      AdminService.removeSelectedTaskRelationships(this.communityAbbreviation, this.taskId, body)
        .then(() => {
          this.$refs.toaster.pop('success', 'Relationships removed successfully')
          return this.load()
        })
        .catch(e => {
          this.$refs.toaster.pop('error', 'An error has ocurred')
        })
        .finally(() => {
          this.loading = false
        })
    },
    load() {
      return AdminService.getTaskRelationships(this.communityAbbreviation, this.taskId)
        .then(response => {
          this.relationships = response.item2
          this.task = response.item1

          Object.keys(response.item2).forEach(key => {
            this.selected[key] = []
            this.search[key] = ''
          })
        })
        .catch(e => {
          this.$refs.toaster.pop('error', 'An error has ocurred')
        })
    },
    cancel() {
      this.close()
    }
  },
  mounted() {
    this.loading = true
    this.load().finally(() => (this.loading = false))
  }
}
</script>

<style scoped>
.v-dialog__container {
  display: unset;
}
</style>
